/**
 * AppLayout — the single shell for every authenticated page (member + admin).
 *
 * A dark burgundy sidebar (gold accents) on desktop; on mobile it collapses
 * into a top bar with a hamburger that opens the nav as a drawer. The menu is
 * role-aware: everyone sees Inicio + Mi perfil, admins see the admin sections.
 * Admins can also preview the web as a socio ("Ver como socio"): the auth
 * context then reports the MEMBER role, so the menu, the route guards and every
 * page render exactly as a member would see them, with a banner to go back.
 */

import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Role } from "@alzadrach/types";
import { useAuth } from "../auth/AuthContext";
import { Avatar } from "./Avatar";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

// Standard options (everyone): Inicio, Cuentas, Estatutos, Reuniones, then Mi perfil.
const STANDARD_ITEMS: NavItem[] = [
  { to: "/", label: "Inicio", end: true },
  { to: "/cuentas", label: "Cuentas" },
  { to: "/estatutos", label: "Estatutos" },
  { to: "/reuniones", label: "Reuniones" },
  { to: "/perfil", label: "Mi perfil" },
];

// A solo niños socio only gets their dashboard and perfil.
const SOLO_NINOS_ITEMS: NavItem[] = STANDARD_ITEMS.filter((i) => i.to === "/" || i.to === "/perfil");

// Admin-only options, shown below a divider.
const ADMIN_ITEMS: NavItem[] = [
  { to: "/admin/socios", label: "Socios" },
  { to: "/admin/invitados", label: "Invitados" },
  { to: "/admin/temporadas", label: "Años" },
  { to: "/admin/movimientos", label: "Movimientos" },
  { to: "/admin/importar", label: "Importar" },
  { to: "/admin/cuenta-bancaria", label: "Cuenta bancaria" },
];

export default function AppLayout() {
  const { user, logout, isActualAdmin, viewAsMember, setViewAsMember } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === Role.ADMIN;

  function startMemberPreview() {
    setViewAsMember(true);
    setOpen(false);
    // Admin routes are no longer allowed in the preview; land on the member home.
    navigate("/");
  }

  function endMemberPreview() {
    setViewAsMember(false);
    setOpen(false);
  }

  const renderItem = ({ to, label, end }: NavItem) => (
    <li key={to}>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) => `app-shell__link ${isActive ? "is-active" : ""}`}
        onClick={() => setOpen(false)}
      >
        {label}
      </NavLink>
    </li>
  );

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="app-shell">
      {/* Mobile top bar */}
      <header className="app-shell__topbar">
        <img className="app-shell__lockup-topbar" src="/logo-lockup.png" alt="Filà Alzadrach" />
        <button
          className="app-shell__hamburger"
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "✕" : "☰"}
        </button>
      </header>

      {open && <div className="app-shell__backdrop" onClick={() => setOpen(false)} />}

      {/* Sidebar (desktop) / drawer (mobile) */}
      <aside className={`app-shell__sidebar ${open ? "is-open" : ""}`}>
        <div className="app-shell__brand-block">
          <img className="app-shell__lockup" src="/logo-lockup.png" alt="Filà Alzadrach" />
          <span className="app-shell__role">
            {isAdmin ? "Administración" : viewAsMember ? "Vista como socio" : "Socio"}
          </span>
        </div>

        <nav className="app-shell__nav" aria-label="Navegación principal">
          <ul>
            {(user?.access === "ninos" ? SOLO_NINOS_ITEMS : STANDARD_ITEMS).map(renderItem)}
            {isAdmin && (
              <>
                <li className="app-shell__divider" aria-hidden="true" />
                {ADMIN_ITEMS.map(renderItem)}
                <li>
                  <button type="button" className="app-shell__link app-shell__link-button" onClick={startMemberPreview}>
                    Ver como socio
                  </button>
                </li>
              </>
            )}
            {viewAsMember && (
              <>
                <li className="app-shell__divider" aria-hidden="true" />
                <li>
                  <button type="button" className="app-shell__link app-shell__link-button" onClick={endMemberPreview}>
                    Volver a administración
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>

        <div className="app-shell__footer">
          {user && (
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-1)" }}>
              <Avatar name={user.fullName || user.username} src={user.avatar} size={32} />
              <span className="app-shell__email">{user.fullName || user.username}</span>
            </div>
          )}
          <button type="button" className="app-shell__logout" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Page content */}
      <main className="app-shell__content">
        {isActualAdmin && viewAsMember && (
          <div className="app-shell__preview-banner" role="status">
            <span>Estás viendo la web como socio.</span>
            <button type="button" className="btn btn-sm btn-secondary" onClick={endMemberPreview}>
              Volver a administración
            </button>
          </div>
        )}
        <Outlet />
      </main>

      <style>{`
        .app-shell { display: flex; min-height: 100vh; background: var(--color-surface-alt); }

        .app-shell__topbar { display: none; }
        .app-shell__hamburger,
        .app-shell__backdrop { display: none; }

        .app-shell__sidebar {
          width: var(--sidebar-width);
          flex-shrink: 0;
          background: #0f0f0f;
          display: flex;
          flex-direction: column;
          padding: var(--space-5) 0;
          /* Pin to the viewport so the nav scrolls internally and the footer
             (Cerrar sesión) stays visible regardless of how many items. */
          height: 100vh;
          position: sticky;
          top: 0;
          overflow: hidden;
        }

        .app-shell__brand-block {
          flex-shrink: 0;
          padding: 0 var(--space-6) var(--space-4);
          margin-bottom: var(--space-3);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          display: flex; flex-direction: column; align-items: center; gap: var(--space-2);
        }
        .app-shell__lockup { width: 120px; height: auto; }
        .app-shell__lockup-topbar { height: 48px; width: auto; }
        .app-shell__role { color: rgba(255,255,255,0.5); font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.06em; }

        /* The only scrolling region: grows to fill, scrolls when it can't.
           No visible scrollbar (wheel/touch still scroll): a bar on the dark
           sidebar looks out of place, and the compact spacing below keeps the
           full menu on screen on most viewports anyway. */
        .app-shell__nav {
          flex: 1 1 auto; min-height: 0; overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .app-shell__nav::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .app-shell__nav ul { list-style: none; margin: 0; padding: 0; }
        .app-shell__divider {
          height: 1px;
          margin: var(--space-2) var(--space-6);
          background: rgba(255,255,255,0.14);
        }
        .app-shell__link {
          display: block;
          padding: var(--space-2) var(--space-6);
          font-size: var(--text-sm);
          color: rgba(255,255,255,0.75);
          text-decoration: none;
          border-left: 3px solid transparent;
          transition: background var(--transition-fast), color var(--transition-fast);
        }
        .app-shell__link:hover { background: rgba(255,255,255,0.05); color: #fff; }
        .app-shell__link-button {
          width: 100%; text-align: left; background: transparent; cursor: pointer;
          border-top: none; border-right: none; border-bottom: none;
          font-family: inherit;
        }
        .app-shell__preview-banner {
          display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap;
          margin-bottom: var(--space-6);
          padding: var(--space-3) var(--space-4);
          background: var(--color-gold-100, #fdf3d7);
          border: 1px solid var(--color-gold-400, #d4a73a);
          border-radius: var(--radius-md);
          font-size: var(--text-sm);
        }
        .app-shell__link.is-active {
          color: var(--color-gold-300);
          background: rgba(255,255,255,0.08);
          border-left-color: var(--color-gold-400);
          font-weight: var(--font-semibold);
        }

        .app-shell__footer {
          flex-shrink: 0;
          padding: var(--space-4) var(--space-6) 0;
          border-top: 1px solid rgba(255,255,255,0.1);
          margin-top: var(--space-3);
          display: flex; flex-direction: column; gap: var(--space-2);
        }
        .app-shell__email { font-size: var(--text-xs); color: rgba(255,255,255,0.45); word-break: break-all; }
        .app-shell__logout {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.85);
          padding: var(--space-2) var(--space-3);
          border-radius: var(--radius-md);
          font-size: var(--text-sm);
          cursor: pointer;
          transition: background var(--transition-fast), color var(--transition-fast);
        }
        .app-shell__logout:hover { background: rgba(255,255,255,0.12); color: #fff; }

        .app-shell__content {
          flex: 1;
          min-width: 0;
          padding: var(--space-8);
          max-width: var(--max-width-xl);
          overflow-y: auto;
        }

        @media (max-width: 767px) {
          .app-shell { flex-direction: column; }

          .app-shell__topbar {
            display: flex; align-items: center; justify-content: space-between;
            padding: var(--space-3) var(--space-4);
            background: #0f0f0f;
            position: sticky; top: 0; z-index: 30;
          }
          .app-shell__hamburger {
            display: block; background: transparent; border: none;
            color: #fff; font-size: 1.5rem; line-height: 1; cursor: pointer;
          }

          .app-shell__sidebar {
            position: fixed; top: 0; left: 0; bottom: 0;
            width: min(82vw, 280px);
            transform: translateX(-100%);
            transition: transform 0.25s ease;
            z-index: 40; overflow: hidden;
          }
          .app-shell__sidebar.is-open { transform: translateX(0); }

          .app-shell__backdrop {
            display: block; position: fixed; inset: 0;
            background: rgba(0,0,0,0.5); z-index: 35;
          }

          .app-shell__content { padding: var(--space-5) var(--space-4); max-width: 100%; }
        }
      `}</style>
    </div>
  );
}
